import { cn } from '../lib/utils'
import Skeleton from './Skeleton'

/**
 * A section heading (`text-base leading-base`, 4px below) and its description
 * (same type, 20px below) — the shape both sections of the form open with.
 *
 * Widths are a share of the column rather than pixels, because this panel is a
 * `md` Modal: 384px at the `md` breakpoint through 770px at `3xl`, and the
 * viewport width on a phone. One line box per string, which is what every
 * string measures to from `md` up; a phone narrow enough to wrap the longer
 * description reflows by one line on arrival.
 */
function SectionHeaderSkeleton({
  headingClassName,
  descriptionClassName,
}: {
  headingClassName: string
  descriptionClassName: string
}) {
  return (
    <>
      <div className="mb-4 flex h-24 items-center">
        <Skeleton h={16} className={headingClassName} />
      </div>
      <div className="mb-20 flex h-24 items-center">
        <Skeleton h={16} className={descriptionClassName} />
      </div>
    </>
  )
}

/**
 * One of the two email preference cards: the same
 * `border rounded-md p-[19px]` box, a label row and a description row in the
 * left column, and the 16px checkbox indicator pushed to the right edge.
 */
function PreferenceCardSkeleton() {
  return (
    <div className="relative flex items-center justify-between rounded-md border border-border p-[19px]">
      <div className="flex flex-1 flex-col gap-4">
        {/* Label — `text-base leading-base`, so a 16px bar in a 24px box. */}
        <div className="flex h-24 items-center">
          <Skeleton h={16} className="w-[27%]" />
        </div>
        {/* Description — `text-sm leading-sm`, a 14px bar in a 20px box. */}
        <div className="flex h-20 items-center">
          <Skeleton h={14} className="w-[71%]" />
        </div>
      </div>
      <Skeleton w={16} h={16} className="ml-16 shrink-0" />
    </div>
  )
}

/**
 * The Communication panel's loading state (RP-5936).
 *
 * It mirrors the form's own shell — the same `pt-20`, the same scroll column
 * and the same bordered footer — so the panel keeps its shape from the first
 * frame and the save bar does not appear only once preferences land.
 *
 * Rendered for the Customer Hub panel only; the standalone page reached from
 * an email keeps its centred `Loader` (see `PreferenceCenterCore`).
 */
export default function PreferenceCenterSkeleton({
  isReserved,
}: {
  /** Reserve the layout without painting it (the anti-flash window). */
  isReserved?: boolean
}) {
  return (
    <div
      className={cn('flex flex-col pt-20 h-full', isReserved && 'invisible')}
      aria-busy="true"
      data-testid="preference-center-skeleton"
    >
      <div className="flex flex-1 flex-col items-center px-16 lg:px-20 3xl:px-120 overflow-y-auto">
        <div className="w-full flex flex-col gap-40 pt-20 pb-60">
          <div className="w-full">
            <SectionHeaderSkeleton
              headingClassName="w-[20%]"
              descriptionClassName="w-[64%]"
            />
            {/* The language `SelectField` trigger — 44px tall, `rounded-md`. */}
            <Skeleton h={44} r={4} className="w-full" />
          </div>

          <div className="w-full">
            <SectionHeaderSkeleton
              headingClassName="w-[12%]"
              descriptionClassName="w-[93%]"
            />
            <div className="flex flex-col gap-12">
              <PreferenceCardSkeleton />
              <PreferenceCardSkeleton />
            </div>
          </div>

          {/* Disclaimer — `text-xs leading-xs`, centred in a 246px column and
              wrapping onto three contiguous lines (no gap: it is one
              paragraph, so the line boxes stack directly). */}
          <div className="mx-auto flex w-[246px] max-w-full flex-col items-center">
            <div className="flex h-16 w-full items-center">
              <Skeleton h={12} className="w-full" />
            </div>
            <div className="flex h-16 w-full items-center">
              <Skeleton h={12} className="w-full" />
            </div>
            <div className="flex h-16 w-full items-center justify-center">
              <Skeleton h={12} className="w-[55%]" />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border p-16 lg:p-20 flex flex-col gap-12">
        {/* The save button — `size="l"`, so 44px and `rounded-md`. */}
        <Skeleton h={44} r={4} className="w-full" />
      </div>
    </div>
  )
}
